import BigNumber from 'bignumber.js';
import cn from 'classnames';

export interface ValueBreakdownContextData {
    value: number | null;
    unsettled: number;
    show: string;
    formatter: Intl.NumberFormat;
    isGroupSummary: boolean;
}

const ValueBreakdownContext = ({ value, unsettled, show, formatter, isGroupSummary }: ValueBreakdownContextData) => {
    const openPnl = BigNumber(value || 0)
        .minus(unsettled || 0)
        .toNumber();
    const formattedValue = formatter.format(value ?? 0);

    return (
        <div className="flex flex-col space-y-2">
            <div>Value Breakdown</div>
            <div className="px-1 space-y-1">
                <BreakdownEntry
                    label="Unsettled PnL"
                    value={unsettled}
                    colorValue={value}
                    formattedColorValue={formattedValue}
                    show={show}
                    formatter={formatter}
                    isGroupSummary={isGroupSummary}
                />
                <BreakdownEntry
                    label="Open PnL"
                    value={openPnl}
                    colorValue={openPnl}
                    formattedColorValue={formatter.format(openPnl)}
                    show={show}
                    formatter={formatter}
                    isGroupSummary={isGroupSummary}
                />
            </div>
            <div className="text-xs text-brand-neutral-400">
                Unsettled PnL is factored into the total calculation, where Open PnL reflects the current open positions
            </div>
        </div>
    );
};

const BreakdownEntry = ({
    label,
    value,
    colorValue,
    formattedColorValue,
    show,
    formatter,
    isGroupSummary
}: {
    label: string;
    value: number;
    colorValue: number | null;
    formattedColorValue: string;
    show: string;
    formatter: Intl.NumberFormat;
    isGroupSummary: boolean;
}) => {
    const formattedValue = formatter.format(value);

    return (
        <div className="flex gap-3 items-center text-xs text-brand-neutral-200">
            <span className="w-24">{label}</span>
            <span>:</span>
            <div
                className={cn('text-xs', {
                    'text-brand-green': colorValue !== null && colorValue > 0 && formattedColorValue !== '0',
                    'text-brand-red': colorValue !== null && colorValue < 0 && formattedColorValue !== '-0',
                    'font-semibold': isGroupSummary
                })}>
                {value === null || isNaN(value) ? '-' : `${show} ${formattedValue === '-0' ? '0' : formattedValue}`}
            </div>
        </div>
    );
};

export default ValueBreakdownContext;
